import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { galleryData } from '../constants/galleryImages';

const ImagesPage: React.FC = () => {
  const { t } = useTranslation();
  
  const [modalOpen, setModalOpen] = useState(false);
  const [currentGroup, setCurrentGroup] = useState<string[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);

  const openModal = (group: string[], index: number) => {
    setCurrentGroup(group);
    setCurrentIndex(index);
    setModalOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    setModalOpen(false);
    document.body.style.overflow = 'auto';
  };

  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!modalOpen) return;
      if (e.key === 'ArrowRight') {
        setCurrentIndex((prev) => (prev + 1) % currentGroup.length);
      } else if (e.key === 'ArrowLeft') {
        setCurrentIndex((prev) => (prev - 1 + currentGroup.length) % currentGroup.length);
      } else if (e.key === 'Escape') {
        closeModal();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [modalOpen, currentGroup.length]);

  const nextImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev + 1) % currentGroup.length);
  };

  const prevImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev - 1 + currentGroup.length) % currentGroup.length);
  };

  const sections = [
    { title: "Okučnica", images: galleryData.root },
    { title: "Apartman 1", images: galleryData['1'] },
    { title: "Apartman 2", images: galleryData['2'] },
    { title: "Apartman 3", images: galleryData['3'] },
    { title: "Apartman 4", images: galleryData['4'] },
    { title: "Prizemlje", images: galleryData.prizemlje }
  ];

  return (
    <div className="images-page container section-container" style={{ padding: '0 1rem' }}>
      <div className="gallery-header" style={{ textAlign: 'center', marginBottom: 'var(--spacing-16)' }}>
        <span className="section-label">{t('navbar.images')}</span>
        <h1>{t('imagesPage.title')}</h1>
        <p style={{ maxWidth: '600px', margin: '0 auto', fontSize: '1.1rem', opacity: 0.8 }}>
          {t('imagesPage.intro')}
        </p>
      </div>

      {sections.map((section, secIdx) => (
        <div key={secIdx} style={{ marginBottom: 'var(--spacing-16)' }}>
          <h2 style={{ marginBottom: 'var(--spacing-8)' }}>{section.title}</h2>
          <div className="editorial-gallery" style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fill, minmax(min(250px, 100%), 1fr))', 
            gap: 'var(--spacing-8)',
            alignItems: 'start'
          }}>
            {section.images.map((src, index) => (
              <div 
                key={index} 
                className="gallery-item hover-scale" 
                onClick={() => openModal(section.images, index)}
                style={{ 
                  borderRadius: 'var(--radius-lg)', 
                  overflow: 'hidden', 
                  boxShadow: 'var(--shadow-ambient)',
                  cursor: 'pointer',
                  aspectRatio: '4/3' // Uniform aspect ratio for grid
                }}
              >
                <img 
                  src={src} 
                  alt={`${section.title} src`} 
                  style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', transition: 'transform 0.3s ease' }} 
                />
              </div>
            ))}
          </div>
        </div>
      ))}

      {modalOpen && currentGroup.length > 0 && (
        <div 
          className="image-modal-overlay" 
          onClick={closeModal}
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.85)',
            zIndex: 9999,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            backdropFilter: 'blur(5px)'
          }}
        >
          <div 
            className="image-modal-content"
            onClick={(e) => e.stopPropagation()} // Prevent close when clicking inner wrapper
            style={{
              position: 'relative',
              width: '90vw',
              height: '90vh',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <button 
              onClick={closeModal}
              style={{
                position: 'absolute', top: '10px', right: '10px',
                background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: '50%',
                color: 'white', width: '40px', height: '40px', fontSize: '20px',
                cursor: 'pointer', zIndex: 10
              }}
            >
              ✕
            </button>
            <button 
              onClick={prevImage}
              style={{
                position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)',
                background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: '50%',
                color: 'white', width: '50px', height: '50px', fontSize: '24px',
                cursor: 'pointer', zIndex: 10
              }}
            >
              ❮
            </button>
            <img 
              src={currentGroup[currentIndex]} 
              alt="Modal Preview" 
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain',
                borderRadius: '8px',
                boxShadow: '0 10px 30px rgba(0,0,0,0.5)'
              }} 
            />
            <button 
              onClick={nextImage}
              style={{
                position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)',
                background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: '50%',
                color: 'white', width: '50px', height: '50px', fontSize: '24px',
                cursor: 'pointer', zIndex: 10
              }}
            >
              ❯
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImagesPage; 